import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AnalyticsEvent, AnalyticsService, ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { catchError, Observable, of, tap } from 'rxjs';
import {
  CreateCriticalDates,
  CriticalDateReasonCode,
  CriticalDates,
  CriticalDatesFinalTentativeDue,
  UpdateFirm,
  UpdateFirstTentative,
  UpdateOccupancyTerms,
  UpdateSecondTentative
} from '@zayan-erp/sales';
import { SalesApsService } from './sales-aps.service';

@Injectable({
  providedIn: 'root'
})
export class CriticalDatesService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);
  baseUrl = this.#apiConfig.baseUrlV2;

  getCriticalDates(dealId: string): Observable<CriticalDates | null> {
    return this.#http
      .getData<CriticalDates>(`${this.baseUrl}/deals/${dealId}/critical-dates`, {
        showLoader: true,
        showMessage: false,
        showError: false
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          throw error;
        })
      );
  }

  getReasonCodes(): Observable<CriticalDateReasonCode[]> {
    return this.#http.getData<CriticalDateReasonCode[]>(`${this.baseUrl}/lookup/critical-date-reason-codes`, {
      showLoader: false,
      showMessage: false
    });
  }

  getFinalTentativeDue(
    dealId: string,
    roofAssemblyDate?: string | null
  ): Observable<CriticalDatesFinalTentativeDue | null> {
    const params = roofAssemblyDate ? new HttpParams().set('roofAssemblyDate', roofAssemblyDate) : undefined;
    return this.#http
      .getData<CriticalDatesFinalTentativeDue>(`${this.baseUrl}/deals/${dealId}/critical-dates/final-tentative-due`, {
        showLoader: false,
        showMessage: false,
        showError: false,
        options: params ? { params } : undefined
      })
      .pipe(catchError(() => of(null)));
  }

  createCriticalDates(payload: CreateCriticalDates, dealId: string): Observable<CriticalDates> {
    return this.#http
      .postData<CriticalDates>(
        `${this.baseUrl}/deals/${dealId}/critical-dates`,
        { showLoader: true, buttonId: 'deal-update-critical-dates', showMessage: true },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.CriticalDateCreated, this.#salesAps.dealCtx(dealId));
        })
      );
  }

  updateFirstTentative(payload: UpdateFirstTentative, dealId: string, showMessage = true): Observable<CriticalDates> {
    return this.#http
      .putData<CriticalDates>(`${this.baseUrl}/deals/${dealId}/critical-dates/first-tentative`, payload, {
        showLoader: true,
        buttonId: 'deal-update-critical-dates',
        showMessage: true
      })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.CriticalDateUpdated, {
            ...this.#salesAps.dealCtx(dealId),
            section: 'first_tentative'
          });
        })
      );
  }

  updateSecondTentative(payload: UpdateSecondTentative, dealId: string, showMessage = true): Observable<CriticalDates> {
    return this.#http
      .putData<CriticalDates>(`${this.baseUrl}/deals/${dealId}/critical-dates/second-tentative`, payload, {
        showLoader: true,
        buttonId: 'deal-update-critical-dates',
        showMessage: true
      })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.CriticalDateUpdated, {
            ...this.#salesAps.dealCtx(dealId),
            section: 'second_tentative'
          });
        })
      );
  }

  updateFirm(payload: UpdateFirm, dealId: string, showMessage = true): Observable<CriticalDates> {
    return this.#http
      .putData<CriticalDates>(`${this.baseUrl}/deals/${dealId}/critical-dates/firm`, payload, {
        showLoader: true,
        buttonId: 'deal-update-critical-dates',
        showMessage: true
      })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.CriticalDateUpdated, {
            ...this.#salesAps.dealCtx(dealId),
            section: 'firm'
          });
        })
      );
  }

  updateOccupancyTerms(payload: UpdateOccupancyTerms, dealId: string, showMessage = true): Observable<CriticalDates> {
    return this.#http
      .putData<CriticalDates>(`${this.baseUrl}/deals/${dealId}/critical-dates/occupancy-terms`, payload, {
        showLoader: true,
        showMessage,
        showError: false
      })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.CriticalDateUpdated, {
            ...this.#salesAps.dealCtx(dealId),
            section: 'occupancy_terms'
          });
        })
      );
  }
}
